import { useState, type KeyboardEvent } from 'react'
import { X } from 'lucide-react'
import clsx from 'clsx'

export interface PageNumbersInputProps {
  value: number[]
  onChange: (value: number[]) => void
  disabled?: boolean
}

// Chip list for search_pages (schema: integers >= 1). Same visual language
// as ChipInput, but numeric-parsed/deduped/sorted rather than free text -
// kept local to this feature since no other screen needs page numbers yet.
export function PageNumbersInput({ value, onChange, disabled }: PageNumbersInputProps) {
  const [draft, setDraft] = useState('')

  function addPage() {
    const page = Number(draft.trim())
    if (!Number.isInteger(page) || page < 1) {
      setDraft('')
      return
    }
    if (!value.includes(page)) {
      onChange([...value, page].sort((a, b) => a - b))
    }
    setDraft('')
  }

  function removePage(page: number) {
    onChange(value.filter((p) => p !== page))
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault()
      addPage()
    }
  }

  return (
    <div
      className={clsx(
        'flex flex-wrap items-center gap-2 rounded-control border border-border bg-bg p-2',
        disabled && 'opacity-50',
      )}
    >
      {value.map((page) => (
        <span
          key={page}
          className="flex items-center gap-1 rounded-pill bg-surface-raised px-2 py-1 text-meta text-text-primary"
        >
          {page}
          {!disabled && (
            <button
              type="button"
              aria-label={`Remove page ${page}`}
              onClick={() => removePage(page)}
              className="text-text-secondary hover:text-text-primary"
            >
              <X size={12} strokeWidth={1.5} />
            </button>
          )}
        </span>
      ))}
      <input
        type="number"
        min={1}
        value={draft}
        placeholder="Add page…"
        disabled={disabled}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        className="min-w-[80px] flex-1 bg-transparent text-body text-text-primary outline-none placeholder:text-text-disabled"
      />
    </div>
  )
}
